import type {
  TutorWeekday,
  TutorWeeklyWorkingHours,
  TutorWorkingHoursDay,
} from '../models/Tutor';
import type { SpaceScheduleWeeklyItem, SpaceWeeklySchedule } from '../models/Space';

/** Weekday order used by the editor and summary views. */
export const TUTOR_WEEKDAYS: TutorWeekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

/** DOM id / URL hash for deep-linking to the working hours editor. */
export const TUTOR_WORKING_HOURS_SECTION_ID = 'working-hours';

/** Form-state shape for a single day. Times are stored as `HH:mm`. */
export interface TutorWorkingHoursDayDraft {
  periodStart: string;
  periodEnd: string;
  openAllDay: boolean;
  dayOff: boolean;
}

export type TutorWorkingHoursDraft = Record<TutorWeekday, TutorWorkingHoursDayDraft>;

export interface TutorWorkingHoursDayFieldError {
  periodStart?: string;
  periodEnd?: string;
}

export type TutorWorkingHoursFieldErrors = Partial<
  Record<TutorWeekday, TutorWorkingHoursDayFieldError>
>;

const DEFAULT_PERIOD_START = '09:00';
const DEFAULT_PERIOD_END = '17:00';

/** Default IANA zone for tutors without a saved timezone (Band Space is Poland-first). */
export const DEFAULT_TUTOR_TIMEZONE = 'Europe/Warsaw';

/** Normalizes an API time (`HH:mm:ss` or `HH:mm`) into the `HH:mm` form value. */
export function toHourMinute(value: string | null | undefined): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  const hours = match[1].padStart(2, '0');
  return `${hours}:${match[2]}`;
}

/**
 * Normalizes an `HH:mm` form value into the backend time shape.
 * The tutor listing endpoint expects `HH:mm` (Jackson rejects `HH:mm:ss`).
 */
export function toApiTime(value: string): string | null {
  const hourMinute = toHourMinute(value);
  return hourMinute || null;
}

function createDefaultDayDraft(dayOff = false): TutorWorkingHoursDayDraft {
  return {
    periodStart: dayOff ? '' : DEFAULT_PERIOD_START,
    periodEnd: dayOff ? '' : DEFAULT_PERIOD_END,
    openAllDay: false,
    dayOff,
  };
}

/** Mon–Fri working, weekend off — used when no working hours are present. */
export function createDefaultTutorWorkingHoursDraft(): TutorWorkingHoursDraft {
  return {
    monday: createDefaultDayDraft(),
    tuesday: createDefaultDayDraft(),
    wednesday: createDefaultDayDraft(),
    thursday: createDefaultDayDraft(),
    friday: createDefaultDayDraft(),
    saturday: createDefaultDayDraft(true),
    sunday: createDefaultDayDraft(true),
  };
}

function dayDraftFromApi(day: TutorWorkingHoursDay | null | undefined): TutorWorkingHoursDayDraft {
  if (!day) {
    return createDefaultDayDraft(true);
  }
  const dayOff = day.dayOff === true;
  const openAllDay = !dayOff && day.openAllDay === true;
  return {
    periodStart: dayOff || openAllDay ? '' : toHourMinute(day.periodStart),
    periodEnd: dayOff || openAllDay ? '' : toHourMinute(day.periodEnd),
    openAllDay,
    dayOff,
  };
}

/** Builds editor form state from a backend working-hours payload. */
export function tutorWorkingHoursDraftFromApi(
  workingHours: TutorWeeklyWorkingHours | null | undefined
): TutorWorkingHoursDraft {
  if (!workingHours) {
    return createDefaultTutorWorkingHoursDraft();
  }
  return TUTOR_WEEKDAYS.reduce((acc, weekday) => {
    acc[weekday] = dayDraftFromApi(workingHours[weekday]);
    return acc;
  }, {} as TutorWorkingHoursDraft);
}

function serializeDay(draft: TutorWorkingHoursDayDraft): TutorWorkingHoursDay {
  if (draft.dayOff) {
    return { dayOff: true };
  }
  if (draft.openAllDay) {
    return { openAllDay: true };
  }
  return {
    periodStart: toApiTime(draft.periodStart),
    periodEnd: toApiTime(draft.periodEnd),
  };
}

/** Maps editor form state to the backend weekly working-hours request shape. */
export function serializeTutorWorkingHours(
  draft: TutorWorkingHoursDraft
): TutorWeeklyWorkingHours {
  return TUTOR_WEEKDAYS.reduce((acc, weekday) => {
    acc[weekday] = serializeDay(draft[weekday]);
    return acc;
  }, {} as TutorWeeklyWorkingHours);
}

/** Defensive normalization so unknown/partial payloads stay renderable. */
export function normalizeTutorWorkingHours(
  workingHours: TutorWeeklyWorkingHours | null | undefined
): TutorWeeklyWorkingHours | null {
  if (!workingHours || typeof workingHours !== 'object') {
    return null;
  }
  const normalized: TutorWeeklyWorkingHours = {};
  let hasAny = false;
  for (const weekday of TUTOR_WEEKDAYS) {
    const day = workingHours[weekday];
    if (day == null || typeof day !== 'object') continue;
    hasAny = true;
    if (day.dayOff === true) {
      normalized[weekday] = { dayOff: true };
    } else if (day.openAllDay === true) {
      normalized[weekday] = { openAllDay: true };
    } else {
      normalized[weekday] = {
        periodStart: typeof day.periodStart === 'string' ? day.periodStart : null,
        periodEnd: typeof day.periodEnd === 'string' ? day.periodEnd : null,
      };
    }
  }
  return hasAny ? normalized : null;
}

const TIMEZONE_PATTERN = /^[A-Za-z][A-Za-z0-9+_-]*(?:\/[A-Za-z0-9+_-]+)+$/;

/** Trims a timezone value; returns null when blank. */
export function normalizeTutorTimezone(timezone: string | null | undefined): string | null {
  if (typeof timezone !== 'string') return null;
  const trimmed = timezone.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Editor/display zone: saved value, otherwise Europe/Warsaw. */
export function resolveTutorTimezoneOrDefault(timezone: string | null | undefined): string {
  const normalized = normalizeTutorTimezone(timezone);
  if (!normalized || normalized === 'UTC') {
    return DEFAULT_TUTOR_TIMEZONE;
  }
  return normalized;
}

/**
 * Lightweight IANA-style validation. Canonical validation stays on the backend;
 * this only catches obvious typos before submission.
 */
export function isValidTutorTimezone(timezone: string | null | undefined): boolean {
  const normalized = normalizeTutorTimezone(timezone);
  if (!normalized) return false;
  if (!TIMEZONE_PATTERN.test(normalized) && normalized !== 'UTC') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: normalized });
    return true;
  } catch {
    return false;
  }
}

/**
 * Validates obvious schedule mistakes client-side. Overlapping intervals and
 * inherited-default semantics are confirmed by the backend.
 */
export function getTutorWorkingHoursFieldErrors(
  draft: TutorWorkingHoursDraft,
  translate: (key: string, fallback: string) => string
): TutorWorkingHoursFieldErrors {
  const errors: TutorWorkingHoursFieldErrors = {};
  for (const weekday of TUTOR_WEEKDAYS) {
    const day = draft[weekday];
    if (day.dayOff || day.openAllDay) continue;
    const dayError: TutorWorkingHoursDayFieldError = {};
    if (!day.periodStart) {
      dayError.periodStart = translate(
        'tutor.workingHours.startRequired',
        'Start time is required'
      );
    }
    if (!day.periodEnd) {
      dayError.periodEnd = translate('tutor.workingHours.endRequired', 'End time is required');
    }
    if (
      day.periodStart &&
      day.periodEnd &&
      toHourMinute(day.periodStart) >= toHourMinute(day.periodEnd)
    ) {
      dayError.periodEnd = translate(
        'tutor.workingHours.endAfterStart',
        'End time must be after start time'
      );
    }
    if (Object.keys(dayError).length > 0) {
      errors[weekday] = dayError;
    }
  }
  return errors;
}

export function hasTutorWorkingHoursErrors(errors: TutorWorkingHoursFieldErrors): boolean {
  return Object.keys(errors).length > 0;
}

export function getTutorWeekdayLabel(
  weekday: TutorWeekday,
  translate: (key: string, fallback: string) => string
): string {
  const fallbacks: Record<TutorWeekday, string> = {
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
  };
  return translate(`space.days.${weekday}`, fallbacks[weekday]);
}

/** Formats a single day for read-only discovery views. */
export function formatTutorWorkingHoursDay(
  day: TutorWorkingHoursDay | null | undefined,
  translate: (key: string, fallback: string) => string
): string {
  if (!day || day.dayOff === true) {
    return translate('tutor.workingHours.closed', 'Closed');
  }
  if (day.openAllDay === true) {
    return translate('tutor.workingHours.open24h', 'Open 24h');
  }
  const start = toHourMinute(day.periodStart);
  const end = toHourMinute(day.periodEnd);
  if (!start || !end) {
    return translate('tutor.workingHours.unavailable', 'Not set');
  }
  return `${start}–${end}`;
}

/** True when at least one day has bookable hours. */
export function hasAnyTutorWorkingHours(
  workingHours: TutorWeeklyWorkingHours | null | undefined
): boolean {
  if (!workingHours) return false;
  return TUTOR_WEEKDAYS.some((weekday) => {
    const day = workingHours[weekday];
    if (!day) return false;
    if (day.dayOff === true) return false;
    if (day.openAllDay === true) return true;
    return Boolean(toHourMinute(day.periodStart) && toHourMinute(day.periodEnd));
  });
}

/** True when the API returned any per-day schedule payload (including all-closed weeks). */
export function hasTutorWorkingHoursSchedule(
  workingHours: TutorWeeklyWorkingHours | null | undefined,
): boolean {
  return normalizeTutorWorkingHours(workingHours) != null;
}

const CLOSED_SPACE_SCHEDULE_DAY: SpaceScheduleWeeklyItem = {
  periodStart: '00:00',
  periodEnd: '00:00',
  openAllDay: false,
  dayOff: true,
};

function tutorDayToSpaceScheduleDay(
  day: TutorWorkingHoursDay | null | undefined
): SpaceScheduleWeeklyItem {
  if (!day || day.dayOff === true) {
    return CLOSED_SPACE_SCHEDULE_DAY;
  }
  if (day.openAllDay === true) {
    return { periodStart: '00:00', periodEnd: '24:00', openAllDay: true, dayOff: false };
  }
  const periodStart = toHourMinute(day.periodStart);
  const periodEnd = toHourMinute(day.periodEnd);
  if (!periodStart || !periodEnd) {
    return CLOSED_SPACE_SCHEDULE_DAY;
  }
  return { periodStart, periodEnd, openAllDay: false, dayOff: false };
}

/**
 * Maps effective tutor working hours into the `SpaceWeeklySchedule` shape used by
 * the shared booking calendar (`TimeAndDateStep`).
 */
export function tutorWorkingHoursToSpaceWeeklySchedule(
  workingHours: TutorWeeklyWorkingHours
): SpaceWeeklySchedule {
  return TUTOR_WEEKDAYS.reduce((acc, weekday) => {
    acc[weekday] = tutorDayToSpaceScheduleDay(workingHours[weekday]);
    return acc;
  }, {} as SpaceWeeklySchedule);
}
